import { Illustration, Text } from "@clipboard-health/ui-components";
import { Box, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { type PlacementCandidate } from "@src/appV2/redesign/PlacementCandidate/types";
import { type InterviewWithDetails } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useState } from "react";

import { useUpdateInterview, WORKER_OUTCOME_RESULT } from "../../api/useUpdateInterview";
import { FEEDBACK_STEP, type FeedbackStep } from "./feedbackStep";

interface WorkplaceInterviewFeedbackContentProps {
  interview: InterviewWithDetails;
  onOptionSelected: (step: FeedbackStep) => void;
  refetchInterviews: () => Promise<unknown>;
  isFetchingInterviews: boolean;
  placementCandidate: PlacementCandidate;
}

export function WorkplaceInterviewFeedbackContent(props: WorkplaceInterviewFeedbackContentProps) {
  const {
    interview,
    onOptionSelected,
    refetchInterviews,
    isFetchingInterviews,
    placementCandidate,
  } = props;

  const [selectedOption, setSelectedOption] = useState<FeedbackStep>();
  const { mutateAsync: updateInterview, isLoading: isUpdateInterviewLoading } =
    useUpdateInterview();

  const eventContext = {
    placementCandidate,
    interview,
  };

  useLogEffect(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_VIEWED, eventContext);

  const options = [
    {
      label: "I got an offer 🎉",
      option: FEEDBACK_STEP.gotOffer,
      outcome: WORKER_OUTCOME_RESULT.gotOffer,
    },
    {
      label: "I haven’t heard back about my offer",
      option: FEEDBACK_STEP.notHeardBack,
      outcome: WORKER_OUTCOME_RESULT.notHeardBack,
    },
  ];
  return (
    <>
      <Stack
        spacing={8}
        alignItems="center"
        justifyContent="center"
        sx={{ paddingX: 8, paddingY: 6 }}
      >
        <Illustration type="facility" />
        <Stack spacing={6} textAlign="center">
          <Text variant="h2">How was your interview?</Text>
          <Text>
            Let us know how things went at{" "}
            <Box sx={{ fontWeight: 800 }} component="span">
              {interview.workplace?.name}
            </Box>
            .
          </Text>
          <Text>Select an option below.</Text>
        </Stack>
      </Stack>
      <DialogFooter orientation="horizontal" sx={{ px: 8, pb: 8, pt: 2 }}>
        <Stack spacing={4} width="100%">
          {options.map((option) => (
            <LoadingButton
              key={option.option}
              variant="outlined"
              size="large"
              isLoading={selectedOption === option.option}
              disabled={isUpdateInterviewLoading || isFetchingInterviews}
              onClick={async () => {
                logEvent(
                  APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_OPTION_CLICKED,
                  {
                    ...eventContext,
                    option: option.option,
                  }
                );
                setSelectedOption(option.option);
                await updateInterview({
                  interviewId: interview.id,
                  attributes: { workerOutcome: { result: option.outcome } },
                });
                await refetchInterviews();
                onOptionSelected(option.option);
              }}
            >
              {option.label}
            </LoadingButton>
          ))}
          <Button
            variant="outlined"
            size="large"
            disabled={isUpdateInterviewLoading || isFetchingInterviews}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_OPTION_CLICKED, {
                ...eventContext,
                option: FEEDBACK_STEP.notOfferTheRole,
              });
              onOptionSelected(FEEDBACK_STEP.notOfferTheRole);
            }}
          >
            I wasn’t offered the role
          </Button>
          <Button
            variant="outlined"
            size="large"
            disabled={isUpdateInterviewLoading || isFetchingInterviews}
            onClick={() => {
              logEvent(APP_V2_APP_EVENTS.PLACEMENT_INTERVIEW_ATTENDANCE_WORKPLACE_OPTION_CLICKED, {
                ...eventContext,
                option: FEEDBACK_STEP.notInterview,
              });
              onOptionSelected(FEEDBACK_STEP.notInterview);
            }}
          >
            I didn’t interview
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
}
